import LeagueManagementPanel from "../../leagues/organizer/components/LeagueManagementPanel";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import "react-international-phone/style.css";
import "./AdminPage.css";
import AdminSection from "../../../shared/components/AdminSection";
import AppLayout from "../../../shared/components/AppLayout";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import Toast from "../../../shared/components/Toast";
import { useAuth } from "../../auth/shared/hooks/useAuth";
import {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeHistoryItem,
  ChallengeStatus,
  ChallengeType,
  UpdateChallengePayload,
} from "../../challenges/types/challengeAdmin";
import {
  createPairForChallenge,
  updateChallengeById,
  upsertPlayerProfile,
} from "../../challenges/services/challengeAdminService";
import { useSiteContent } from "../../home/shared/hooks/useSiteContent";
import AdminContentPanel from "../components/AdminContentPanel";
import AdminChallengeEditorPanel from "../components/AdminChallengeEditorPanel";
import AdminClubsPanel from "../components/AdminClubsPanel";
import AdminChallengeModalPanel from "../components/AdminChallengeModalPanel";
import AdminDiscountCoupons from "../components/AdminDiscountCoupons";
import AdminFinishedChallengePanel from "../components/AdminFinishedChallengePanel";
import AdminHistoryPanel from "../components/AdminHistoryPanel";
import AdminUsersTab from "../components/AdminUsersTab";
import AdminCategoryRequestsPanel from "../components/AdminCategoryRequestsPanel";
import AdminPaymentsPanel from "../components/AdminPaymentsPanel";
import AdminRegistrationsPanel from "../components/AdminRegistrationsPanel";
import AdminSidebar from "../components/AdminSidebar";
import { useAdminActiveTabLoader } from "../hooks/useAdminActiveTabLoader";
import { useCategoryChangeRequestsAdmin } from "../hooks/useCategoryChangeRequestsAdmin";
import { useAdminChallengeAutoProvision } from "../hooks/useAdminChallengeAutoProvision";
import { useAdminChallengeDeletion } from "../hooks/useAdminChallengeDeletion";
import { useAdminChallengeEditorState } from "../hooks/useAdminChallengeEditorState";
import { useAdminChallengeHistory } from "../hooks/useAdminChallengeHistory";
import { useAdminChallengeLoader } from "../hooks/useAdminChallengeLoader";
import { useAdminChallengePairActions } from "../hooks/useAdminChallengePairActions";
import { useAdminChallengePersistence } from "../hooks/useAdminChallengePersistence";
import { useAdminChallengeParticipantSelection } from "../hooks/useAdminChallengeParticipantSelection";
import { useAdminChallengeRegistrationDerived } from "../hooks/useAdminChallengeRegistrationDerived";
import { useAdminChallengeRegistrationProvisioning } from "../hooks/useAdminChallengeRegistrationProvisioning";
import { useAdminChallengeRegistrationSelectionSync } from "../hooks/useAdminChallengeRegistrationSelectionSync";
import { useAdminChallengeRegistrationToggles } from "../hooks/useAdminChallengeRegistrationToggles";
import { useAdminChallengeRouteSelection } from "../hooks/useAdminChallengeRouteSelection";
import { useAdminChallengeScheduleDraft } from "../hooks/useAdminChallengeScheduleDraft";
import { useAdminChallengeTournamentCategorySelection } from "../hooks/useAdminChallengeTournamentCategorySelection";
import { useAdminContentEditor } from "../hooks/useAdminContentEditor";
import { useAdminEventRegistrationsState } from "../hooks/useAdminEventRegistrationsState";
import { useAdminEventChallengeLinks } from "../hooks/useAdminEventChallengeLinks";
import { useAdminNavigation } from "../hooks/useAdminNavigation";
import { getAdminPageHeaderMeta } from "../hooks/useAdminPageHeaderMeta";
import { useAdminPayments } from "../hooks/useAdminPayments";
import { useAdminChallengeRounds } from "../hooks/useAdminChallengeRounds";
import { useAdminToast } from "../hooks/useAdminToast";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { useAdminChallengeParticipants } from "../hooks/useAdminChallengeParticipants";
import { useAdminRegistrationActions } from "../hooks/useAdminRegistrationActions";
import { useAdminTournamentCategoryModal } from "../hooks/useAdminTournamentCategoryModal";
import { useAdminTournamentPairModalActions } from "../hooks/useAdminTournamentPairModalActions";
import { useAdminSuper8Groups } from "../hooks/useAdminSuper8Groups";
import {
  updateEventRegistrationPreferredSide,
} from "../services/eventRegistrationAdminService";
import {
  buildInternalEventIdByTournament,
  formatSideLabel,
  isFixedPairOnlyChallengeType,
  normalizeChallengeDetail,
  normalizeParticipantCpfs,
  resolveChallengeEventId,
} from "../utils/adminChallengeEditor";
import { getAdminChallengeStartState } from "../utils/adminChallengeStartState";
import TournamentCategoryModal from "../components/TournamentCategoryModal";
import TournamentPairModal from "../components/TournamentPairModal";
import type {
  ChallengeModalMode,
} from "../../challenges/types/challengeDraft";
import {
  categoryGenderLabel,
  categoryLabel,
} from "../../challenges/utils/challengeLabels";
import {
  createPairId,
  normalizeCpf,
  sideChoiceToDraftSide,
} from "../../challenges/utils/challengePairs";
import { downloadShirtSizesCsv } from "../utils/shirtSizeExport";
import { buildChallengePersistencePayload } from "../utils/adminChallengePersistence";
import { parseAvailabilityPeriodLines } from "../../tournamentAvailability/tournamentAvailabilitySettings";

const CHALLENGE_CATEGORIES: ChallengeCategoryCode[] = [
  "7a",
  "6a",
  "5a",
  "4a",
  "3a",
  "2a",
  "open",
  "free",
];

function parseAdminMoneyToCents(value: string) {
  const maxIntegerCents = 2147483647;
  const normalized = String(value || "")
    .trim()
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  const cents = Math.round(parsed * 100);
  return cents <= maxIntegerCents ? cents : 0;
}
const CATEGORY_GENDERS: ChallengeCategoryGender[] = [
  "mixed",
  "open",
  "male",
  "female",
];
const CHALLENGE_TYPES_ONLY: ChallengeType[] = ["challenge", "super8", "poco"];
type AdminPageProps = {
  challengeIdFromRoute?: number;
  challengeRouteMode?: ChallengeModalMode;
  contentRouteMode?: boolean;
  leaguesRouteMode?: boolean;
  historyTypeFilterFromRoute?: ChallengeType;
  challengeRouteParams?: {
    challengeName?: string;
    challengeStatus?: ChallengeStatus;
    challengeType?: ChallengeType;
    challengeParticipantsCount?: number;
    challengeScheduledAt?: string | null;
  };
};

function buildStatusPayloadFromHistoryItem(
  item: ChallengeHistoryItem,
  status: "started" | "finished",
): UpdateChallengePayload {
  const categoryGender = item.categoryGender || null;
  const isMixed = categoryGender === "mixed" || categoryGender === "open";
  const usesRegistrationCategories = item.challengeType === "tournament";

  return {
    groupName: item.groupName,
    challengeName: item.challengeName,
    description: item.description || "",
    address: item.address || "",
    city: item.city || "",
    state: item.state || "",
    eventImageUrl: item.eventImageUrl || null,
    shirtSizesEnabled: Boolean(item.shirtSizesEnabled),
    shirtSizes: item.shirtSizesEnabled ? item.shirtSizes || [] : [],
    categoryGender,
    category: isMixed ? null : item.category,
    categoryMale: isMixed ? item.categoryMale : null,
    categoryFemale: isMixed ? item.categoryFemale : null,
    super8Group: item.challengeType === "super8" ? item.super8Group || null : null,
    participantCpfs: item.participantCpfs || [],
    scheduledAt: item.scheduledAt || null,
    scheduledEndAt: item.scheduledEndAt || null,
    durationSeconds: Number(item.durationSeconds || 0),
    isOfficial: Boolean(item.isOfficial ?? true),
    includeInRanking:
      item.challengeType === "tournament"
        ? Boolean(item.includeInRanking ?? false)
        : Boolean(item.includeInRanking ?? true),
    organizerUserId: item.organizerUserId || null,
    eventIdentityMode: item.eventIdentityMode || "tnp_standard",
    registrationType: item.registrationType || "individual",
    slotLimit: item.slotLimit || null,
    challengeModel: item.challengeModel || "tnp_standard",
    gameType: item.gameType || null,
    gameCount: item.gameCount || null,
    gameDurationMinutes: item.gameDurationMinutes || null,
    courtsCount: item.courtsCount || null,
    playersCount: item.playersCount || null,
    ...(usesRegistrationCategories
      ? {
          tournamentCategories: (item.registrationCategories || []).map(
            (category) => ({
              categoryKey: category.categoryKey,
              categoryName: category.categoryName,
              categoryMale:
                (category.categoryMale as ChallengeCategoryCode | null) || undefined,
              categoryFemale:
                (category.categoryFemale as ChallengeCategoryCode | null) || undefined,
              categoryGender:
                (category.categoryGender as ChallengeCategoryGender | null) ||
                undefined,
              registrationType: category.registrationType || "individual",
              address: category.address || null,
              scheduledAt: category.scheduledAt || null,
              scheduledEndAt: category.scheduledEndAt || null,
              slotLimit: Math.max(2, Number(category.slotLimit || 12)),
            }),
          ),
        }
      : {}),
    status,
  };
}

export default function AdminPage({
  challengeIdFromRoute,
  challengeRouteMode,
  contentRouteMode,
  leaguesRouteMode,
  historyTypeFilterFromRoute,
  challengeRouteParams,
}: AdminPageProps) {
  const t = siteTexts.admin;
  const [categorySaveRevision, setCategorySaveRevision] = useState(0);
  const {
    isAdmin,
    isOrganizer,
    isStaff,
    loading,
    token,
    user: authUser,
  } = useAuth();
  const { content, setContent } = useSiteContent();
  const isTournamentsRouteView = historyTypeFilterFromRoute === "tournament";
  const isTournamentRouteChallenge =
    isTournamentsRouteView ||
    String(challengeRouteParams?.challengeType || "")
      .trim()
      .toLowerCase() === "tournament";

  const {
    activeTab,
    adminNavCollapsed,
    adminNavOpen,
    closeAdminNav,
    contentTab,
    goToAdminListPage,
    navigateAdminTab,
    navigateAdminTournaments,
    selectTab,
    setContentTab,
    toggleAdminDesktopNav,
    toggleAdminNav,
  } = useAdminNavigation({
    challengeIdFromRoute,
    contentRouteMode,
    leaguesRouteMode,
    historyTypeFilterFromRoute,
    isAdmin,
    isStaff,
    isTournamentRouteChallenge,
  });
  const { setToast, showErrorToast, showSuccessToast, toast } =
    useAdminToast();

  const {
    addListItem,
    contentDraft,
    removeHeroImage,
    removeListImage,
    removeListItem,
    saveContent,
    savingContent,
    updateAbout,
    updateHero,
    updateListItem,
  } = useAdminContentEditor({
    content,
    isAdmin,
    setContent,
    showErrorToast,
    showSuccessToast,
    token,
  });

  const {
    buildUserDraft,
    filteredUsers,
    loadUsers,
    newUser,
    onCancelCreateUser,
    onCreateUser,
    onDeleteUser,
    onDismissCreateUserModal,
    onSaveUser,
    setNewUser,
    setShowCreateUserModal,
    setUserDrafts,
    setUserSearch,
    showCreateUserModal,
    userDrafts,
    userSearch,
    users,
    usersLoading,
  } = useAdminUsers({
    authUserId: authUser?.id,
    isAdmin,
    isOrganizer,
    showErrorToast,
    showSuccessToast,
    token,
  });

  const {
    effectiveHistoryTypeFilters,
    filteredHistory,
    historyDateFrom,
    historyDateTo,
    historyLoading,
    historyNameFilter,
    historyOfficialFilter,
    historyOrganizerUserIdFilter,
    historyStatusFilters,
    historyTotal,
    loadHistory,
    organizerUsers,
    setHistoryDateFrom,
    setHistoryDateTo,
    setHistoryNameFilter,
    setHistoryOfficialFilter,
    setHistoryOrganizerUserIdFilter,
    toggleHistoryStatus,
    toggleHistoryType,
  } = useAdminChallengeHistory({
    defaultTypes: CHALLENGE_TYPES_ONLY,
    historyTypeFilterFromRoute,
    isTournamentsRouteView,
    token,
    users,
  });
  const [startingChallengeIdsFromList, setStartingChallengeIdsFromList] =
    useState<Record<number, boolean>>({});
  const [finishingChallengeIdsFromList, setFinishingChallengeIdsFromList] =
    useState<Record<number, boolean>>({});
  const [savingDrawPairs, setSavingDrawPairs] = useState(false);
  const {
    activeTournamentCategoryKey,
    autoProvisionInFlightRef,
    challengeDetail,
    challengeDurationDraft,
    challengeEventDraft,
    challengeModalMode,
    challengeRoundsJson,
    challengeScheduledAtDraft,
    challengeScheduledEndAtDraft,
    confirmDeleteChallengeError,
    confirmDeleteChallengeLoading,
    confirmDeleteChallengeTarget,
    draftPairs,
    draftPlayers,
    drawCpf,
    pairCpf1,
    pairCpf2,
    pairMode,
    removedTournamentPairKeysRef,
    savingChallenge,
    selectedChallengeId,
    setActiveTournamentCategoryKey,
    setChallengeDetail,
    setChallengeDurationDraft,
    setChallengeEventDraft,
    setChallengeModalMode,
    setChallengeRoundsJson,
    setChallengeScheduledAtDraft,
    setChallengeScheduledEndAtDraft,
    setConfirmDeleteChallengeError,
    setConfirmDeleteChallengeLoading,
    setConfirmDeleteChallengeTarget,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setDrawSideChoice,
    setPairCpf1,
    setPairCpf2,
    setPairMode,
    setSavingChallenge,
    setSelectedChallengeId,
    setShowChallengeModal,
    setTournamentCategoryModal,
    setTournamentPairModal,
    setTournamentPairModalSaving,
    showChallengeModal,
    super8RankingPositionByCpf,
    tournamentCategoryModal,
    tournamentDraftCategoryKeyRef,
    tournamentPairModal,
    tournamentPairModalSaving,
  } = useAdminChallengeEditorState();

  const {
    eventChallengeLinkDrafts,
    eventChallengeLinks,
    eventChallengeLinksLoading,
    linkableEventChallengesSorted,
    loadEventLinks,
    onDeleteEventLink,
    onUpsertEventLink,
    setEventChallengeLinkDrafts,
    setEventChallengeLinks,
  } = useAdminEventChallengeLinks({
    showErrorToast,
    showSuccessToast,
    token,
  });
  const {
    adminPaymentMethod,
    challengeEventRegistrations,
    challengeEventRegistrationsLoading,
    eventRegistrations,
    eventRegistrationsLoading,
    eventRegistrationsStatus,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    setAdminPaymentMethod,
    setEventRegistrationsStatus,
  } = useAdminEventRegistrationsState(token);
  const {
    categoryChangeAdminNotes,
    categoryChangeRequests,
    categoryChangeRequestsLoading,
    categoryChangeRequestsStatus,
    categoryChangeResolveLoadingId,
    loadCategoryChangeRequests,
    onResolveCategoryChangeRequest,
    setCategoryChangeAdminNotes,
    setCategoryChangeRequestsStatus,
  } = useCategoryChangeRequestsAdmin({
    isAdmin,
    loadUsers,
    showErrorToast,
    showSuccessToast,
    token,
    usersCount: users.length,
  });
  const {
    asaasOrganizers,
    asaasSettingsDraft,
    loadPaymentsSettings,
    myAsaasWalletDraft,
    onSaveAsaasSettings,
    onSaveMyWallet,
    onSaveOrganizerWallet,
    paymentsLoading,
    setAsaasOrganizers,
    setAsaasSettingsDraft,
    setMyAsaasWalletDraft,
  } = useAdminPayments({
    isAdmin,
    isOrganizer,
    showErrorToast,
    showSuccessToast,
    token,
  });

  useAdminChallengeScheduleDraft({
    challengeDetail,
    setChallengeDurationDraft,
    setChallengeScheduledAtDraft,
    setChallengeScheduledEndAtDraft,
  });

  useAdminActiveTabLoader({
    activeTab,
    isAdmin,
    isOrganizer,
    isStaff,
    loadCategoryChangeRequests,
    loadEventLinks,
    loadEventRegistrations,
    loadHistory,
    loadPaymentsSettings,
    loadUsers,
    showChallengeModal,
    token,
    usersCount: users.length,
    usersLoading,
  });

  const onSelectChallenge = useAdminChallengeLoader({
    eventChallengeLinks,
    isAdmin,
    isOrganizer,
    loadUsers,
    setActiveTournamentCategoryKey,
    setChallengeDetail,
    setChallengeEventDraft,
    setChallengeModalMode,
    setChallengeRoundsJson,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setDrawSideChoice,
    setEventChallengeLinkDrafts,
    setEventChallengeLinks,
    setPairCpf1,
    setPairCpf2,
    setPairMode,
    setSelectedChallengeId,
    setShowChallengeModal,
    showErrorToast,
    token,
    tournamentDraftCategoryKeyRef,
    users,
  });

  const { onCloseChallengeModal, openChallengeRoute } =
    useAdminChallengeRouteSelection({
      challengeIdFromRoute,
      challengeRouteMode,
      goToAdminListPage,
      loading,
      onSelectChallenge,
      selectTab,
      setShowChallengeModal,
      token,
    });

  const isSuper8 = challengeDetail?.challengeType === "super8";
  const isPoco = isFixedPairOnlyChallengeType(challengeDetail?.challengeType);
  const {
    activeRegistrationCategoryKeyForModal,
    activeTournamentCategory,
    challengeTournamentCategories,
    currentPairMode,
    hasRegistrationCategories,
    isTournamentChallenge,
    selectTournamentCategoryForRegistrations,
  } = useAdminChallengeTournamentCategorySelection({
    activeTournamentCategoryKey,
    challengeDetail,
    isPoco,
    isSuper8,
    pairMode,
    setActiveTournamentCategoryKey,
    setChallengeDetail,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setPairCpf1,
    setPairCpf2,
    tournamentDraftCategoryKeyRef,
  });

  const {
    autoCreateMissingRegistrationsAndCharges,
    resolveParticipantCpfsForPersist,
  } = useAdminChallengeRegistrationProvisioning({
    activeRegistrationCategoryKeyForModal,
    challengeEventRegistrations,
    currentPairMode,
    draftPairs,
    draftPlayers,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    token,
  });

  const {
    challengeRegistrationsOpenByEventId,
    challengeRegistrationsToggleLoadingByEventId,
    onToggleChallengeRegistrationsFromList,
  } = useAdminChallengeRegistrationToggles({
    eventChallengeLinks,
    filteredHistory,
    isAdmin,
    isOrganizer,
    loadEventLinks,
    onRegistrationStatusChange: ({ registrationsOpen, tournamentId }) => {
      if (Number(challengeDetail?.id || 0) !== Number(tournamentId || 0)) {
        return;
      }
      setChallengeEventDraft((prev) => ({
        ...prev,
        registrationsOpen,
      }));
    },
    showErrorToast,
    showSuccessToast,
    token,
  });
  const onStartChallengeFromList = useCallback(
    async (item: ChallengeHistoryItem) => {
      if (!token || item.status !== "pending") return;
      setStartingChallengeIdsFromList((current) => ({
        ...current,
        [item.id]: true,
      }));
      try {
        await updateChallengeById(
          token,
          item.id,
          buildStatusPayloadFromHistoryItem(item, "started"),
        );
        await loadHistory();
        showSuccessToast(t.toasts.challengeStartedSuccess);
      } catch (error) {
        showErrorToast(
          error instanceof Error && error.message
            ? error.message
            : t.toasts.challengeStartError,
        );
      } finally {
        setStartingChallengeIdsFromList((current) => {
          const next = { ...current };
          delete next[item.id];
          return next;
        });
      }
    },
    [
      loadHistory,
      showErrorToast,
      showSuccessToast,
      t.toasts.challengeStartError,
      t.toasts.challengeStartedSuccess,
      token,
    ],
  );
  const onFinishChallengeFromList = useCallback(
    async (item: ChallengeHistoryItem) => {
      if (!token || item.status !== "started") return;
      setFinishingChallengeIdsFromList((current) => ({
        ...current,
        [item.id]: true,
      }));
      try {
        await updateChallengeById(
          token,
          item.id,
          buildStatusPayloadFromHistoryItem(item, "finished"),
        );
        await loadHistory();
        showSuccessToast(t.toasts.challengeFinishedSuccess);
      } catch (error) {
        showErrorToast(
          error instanceof Error && error.message
            ? error.message
            : t.toasts.challengeFinishError,
        );
      } finally {
        setFinishingChallengeIdsFromList((current) => {
          const next = { ...current };
          delete next[item.id];
          return next;
        });
      }
    },
    [
      loadHistory,
      showErrorToast,
      showSuccessToast,
      t.toasts.challengeFinishError,
      t.toasts.challengeFinishedSuccess,
      token,
    ],
  );

  const challengeSelectedParticipants = useMemo(
    () => normalizeParticipantCpfs(challengeDetail?.participantCpfs),
    [challengeDetail?.participantCpfs],
  );

  const {
    availableUsersForChallenge,
    availableUsersForTournamentPairModal,
    eligibleCpfSet,
    explainIneligibleUserByCpf,
    nameByCpf,
    resolveChallengePlayerCpfInput,
    selectedCpfsSet,
  } = useAdminChallengeParticipants({
    activeRegistrationCategoryKeyForModal,
    challengeDetail,
    challengeEventRegistrations,
    currentPairMode,
    draftPairs,
    draftPlayers,
    tournamentPairModalCategoryKey: tournamentPairModal?.categoryKey,
    tournamentPairModalRegistrationId: tournamentPairModal?.registration?.id,
    users,
  });

  const autoSaveChallengeResult = useCallback(
    async (nextRounds: any[][]) => {
      if (!token || !challengeDetail || savingChallenge) return;
      const challengeType = String(challengeDetail.challengeType || "")
        .trim()
        .toLowerCase();
      if (challengeType !== "challenge" && challengeType !== "super8") return;
      try {
        setSavingChallenge(true);
        const participantCpfs = resolveParticipantCpfsForPersist(challengeDetail);
        const organizerUserId = Boolean(challengeDetail.isOfficial ?? true)
          ? null
          : (challengeDetail.organizerUserId ?? null);
        const updated = await updateChallengeById(token, challengeDetail.id, {
          ...buildChallengePersistencePayload({
            challengeDetail,
            challengeEventDraft,
            draftPairs,
            allowTnpEventIdentity: isAdmin,
            organizerUserId,
            participantCpfs,
          }),
          finishedAt: challengeDetail.finishedAt,
          rounds: nextRounds,
        });
        const normalized = normalizeChallengeDetail(updated);
        setChallengeDetail(normalized);
        setChallengeRoundsJson(JSON.stringify(normalized.rounds, null, 2));
        await loadHistory();
      } catch (error) {
        showErrorToast(
          error instanceof Error && error.message
            ? error.message
            : siteTexts.admin.toasts.challengeSaveError,
        );
      } finally {
        setSavingChallenge(false);
      }
    },
    [
      challengeDetail,
      challengeEventDraft,
      draftPairs,
      isAdmin,
      loadHistory,
      resolveParticipantCpfsForPersist,
      savingChallenge,
      setChallengeDetail,
      setChallengeRoundsJson,
      setSavingChallenge,
      showErrorToast,
      token,
    ],
  );

  const {
    applyQuickWinnerForRoundMatch,
    pairLabelById,
    resolveRoundPairLabelWithPoints,
    roundsParsed,
    scoreMax,
    updatePocoWinner,
    updateRoundScore,
  } = useAdminChallengeRounds({
    challengeDetail,
    challengeRoundsJson,
    draftPairs,
    isPoco,
    onCompleteScore: autoSaveChallengeResult,
    setChallengeRoundsJson,
  });

  const {
    ensurePlayerProfile,
    persistPairIfNeeded,
    removeRegistrationFromChallengeSelection,
    setParticipantsFromPairs,
    setParticipantsFromPlayers,
  } = useAdminChallengeParticipantSelection({
    activeRegistrationCategoryKeyForModal,
    challengeDetail,
    isTournamentChallenge: hasRegistrationCategories,
    nameByCpf,
    removedTournamentPairKeysRef,
    setChallengeDetail,
    setDraftPairs,
    setDraftPlayers,
  });
  const {
    addTournamentCategoryOnChallenge,
    editTournamentCategoryOnChallenge,
    removeTournamentCategoryOnChallenge,
    saveTournamentCategoryModal,
    updateTournamentCategoryModalDraft,
  } = useAdminTournamentCategoryModal({
    challengeDetail,
    challengeTournamentCategories,
    loadHistory,
    onCategorySaved: () => setCategorySaveRevision((current) => current + 1),
    setChallengeRoundsJson,
    savingChallenge,
    selectTournamentCategoryForRegistrations,
    setActiveTournamentCategoryKey,
    setChallengeDetail,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setPairCpf1,
    setPairCpf2,
    setSavingChallenge,
    setTournamentCategoryModal,
    showErrorToast,
    showSuccessToast,
    tournamentCategoryModal,
    tournamentDraftCategoryKeyRef,
    token,
  });

  useEffect(() => {
    if (!showChallengeModal || !isPoco) return;
    if (pairMode !== "fixed") setPairMode("fixed");
  }, [isPoco, pairMode, setPairMode, showChallengeModal]);

  const manualRegistrationEventIdForChallenge = useMemo(() => {
    const challengeId = Number(challengeDetail?.id || 0);
    if (!Number.isFinite(challengeId) || challengeId <= 0) return "";
    const linkedEntry = Object.values(eventChallengeLinks).find(
      (entry) => Number(entry.tournamentId) === challengeId,
    );
    return String(
      linkedEntry?.eventId ||
        String(challengeDetail?.eventId || "").trim() ||
        buildInternalEventIdByTournament(challengeId),
    ).trim();
  }, [challengeDetail?.eventId, challengeDetail?.id, eventChallengeLinks]);
  const canAddRegistrationsForChallenge = Boolean(
    manualRegistrationEventIdForChallenge,
  );

  useEffect(() => {
    if (!showChallengeModal) return;
    void loadChallengeEventRegistrations(manualRegistrationEventIdForChallenge);
  }, [
    loadChallengeEventRegistrations,
    manualRegistrationEventIdForChallenge,
    showChallengeModal,
  ]);

  const exportChallengeShirtSizes = useCallback(() => {
    const ok = downloadShirtSizesCsv({
      challengeName:
        String(challengeDetail?.challengeName || "").trim() ||
        `Desafio ${challengeDetail?.id || ""}`.trim(),
      registrations: challengeEventRegistrations,
    });
    if (!ok) {
      showErrorToast(t.createChallengePage.toasts.noShirtSizesToExport);
    }
  }, [
    challengeDetail?.challengeName,
    challengeDetail?.id,
    challengeEventRegistrations,
    showErrorToast,
    t.createChallengePage.toasts.noShirtSizesToExport,
  ]);

  const {
    onCopyPaymentLink,
    onDeleteRegistration,
    onMarkPaidRegistration,
    onSaveAvailabilityRestriction,
    onRejectRegistration,
  } = useAdminRegistrationActions({
    adminPaymentMethod,
    isAdmin,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    manualRegistrationEventIdForChallenge,
    removeRegistrationFromChallengeSelection,
    showErrorToast,
    showSuccessToast,
    token,
  });

  const {
    activeTournamentCategoryPairCapacity,
    challengeFixedPairRegistrations,
    challengeIndividualRegistrations,
    challengeRegistrationByCpf,
    challengeRegistrationByPairId,
    getTournamentPairCapacityForCategory,
    tournamentParticipantsSummaryByCategory,
  } = useAdminChallengeRegistrationDerived({
    activeRegistrationCategoryKeyForModal,
    challengeEventRegistrations,
    challengeTournamentCategories,
    countFixedPairAsPairs: isPoco,
    isTournamentChallenge: hasRegistrationCategories,
  });

  const challengeAvailabilityRestrictionPeriods = useMemo(
    () =>
      challengeEventDraft.availabilityRestrictionsEnabled
        ? parseAvailabilityPeriodLines(
            challengeEventDraft.availabilityRestrictionPeriodsText || "",
          )
        : [],
    [
      challengeEventDraft.availabilityRestrictionPeriodsText,
      challengeEventDraft.availabilityRestrictionsEnabled,
    ],
  );
  const challengeAvailabilityRestrictionAmountCents = useMemo(
    () =>
      challengeEventDraft.availabilityRestrictionsEnabled
        ? parseAdminMoneyToCents(
            challengeEventDraft.availabilityRestrictionAmount,
          )
        : 0,
    [
      challengeEventDraft.availabilityRestrictionAmount,
      challengeEventDraft.availabilityRestrictionsEnabled,
    ],
  );

  useAdminChallengeAutoProvision({
    activeRegistrationCategoryKeyForModal,
    autoProvisionInFlightRef,
    canAddRegistrationsForChallenge,
    challengeDetail,
    challengeRegistrationByCpf,
    challengeRegistrationByPairId,
    currentPairMode,
    draftPairs,
    draftPlayers,
    isTournamentChallenge,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    manualRegistrationEventIdForChallenge,
    showChallengeModal,
    showErrorToast,
    token,
  });

  const {
    closeTournamentPairModal,
    openTournamentPairModal,
    submitTournamentPairModal,
  } = useAdminTournamentPairModalActions({
    activeRegistrationCategoryKeyForModal,
    adminPaymentMethod,
    autoProvisionInFlightRef,
    availabilityRestrictionPeriods: challengeAvailabilityRestrictionPeriods,
    challengeDetailId: challengeDetail?.id,
    challengeEventRegistrations,
    challengeTournamentCategories,
    getTournamentPairCapacityForCategory,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    manualRegistrationEventIdForChallenge,
    nameByCpf,
    removedTournamentPairKeysRef,
    resolveChallengePlayerCpfInput,
    setDraftPairs,
    setParticipantsFromPairs,
    setTournamentPairModal,
    setTournamentPairModalSaving,
    showErrorToast,
    showSuccessToast,
    token,
    tournamentPairModal,
    tournamentPairModalSaving,
  });

  useAdminChallengeRegistrationSelectionSync({
    activeRegistrationCategoryKeyForModal,
    challengeDetail,
    challengeFixedPairRegistrations,
    challengeIndividualRegistrations,
    currentPairMode,
    draftPairs,
    isSuper8,
    isTournamentChallenge: hasRegistrationCategories,
    nameByCpf,
    removedTournamentPairKeysRef,
    setDraftPairs,
    setDraftPlayers,
    setParticipantsFromPairs,
    setParticipantsFromPlayers,
    showChallengeModal,
    showErrorToast,
  });

  const {
    super8GroupAPlayers,
    super8PlayersForCurrentChallenge,
    super8WaitingPlayers,
  } = useAdminSuper8Groups({
    draftPlayers,
    isSuper8,
    super8RankingPositionByCpf,
  });

  const {
    canFinishChallenge,
    canGenerateFixedPairRounds,
    canGeneratePlayerBasedRounds,
    canStartChallenge,
    maxDrawPlayersForCurrentChallenge,
    maxFixedPairsForCurrentChallenge,
    minimumParticipants,
  } = getAdminChallengeStartState({
    challengeDetail: isPoco && challengeDetail && activeTournamentCategory?.courtsCount
      ? { ...challengeDetail, courtsCount: activeTournamentCategory.courtsCount, playersCount: activeTournamentCategory.courtsCount * 4 }
      : challengeDetail,
    challengeSelectedParticipantsCount: challengeSelectedParticipants.length,
    draftPairs,
    draftPlayers,
    isPoco,
    isSuper8,
    super8PlayersForCurrentChallenge,
  });

  const {
    addDrawPlayer,
    addFixedPair,
    generateFixedPairRounds,
    generatePlayerBasedRounds,
  } = useAdminChallengePairActions({
    activeRegistrationCategoryKeyForModal: hasRegistrationCategories
      ? activeRegistrationCategoryKeyForModal
      : "",
    draftPairs,
    draftPlayers,
    drawCpf,
    eligibleCpfSet,
    ensurePlayerProfile,
    explainIneligibleUserByCpf,
    challengeCourtsCount:
      challengeDetail?.challengeType === "poco" ||
      (challengeDetail?.challengeModel === "custom" &&
        challengeDetail.challengeType === "challenge")
        ? Math.max(2, Math.floor(Number((isPoco ? activeTournamentCategory?.courtsCount : null) ?? challengeDetail.courtsCount ?? 2)))
        : 2,
    challengeRoundsJson,
    isPoco,
    isSuper8,
    maxDrawPlayersForCurrentChallenge,
    maxFixedPairsForCurrentChallenge,
    nameByCpf,
    pairCpf1,
    pairCpf2,
    persistPairIfNeeded,
    playerAlreadySelectedMessage: t.historyList.playerAlreadySelected,
    resolveChallengePlayerCpfInput,
    selectedCpfsSet,
    setChallengeRoundsJson,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setPairCpf1,
    setPairCpf2,
    setParticipantsFromPairs,
    setParticipantsFromPlayers,
    showErrorToast,
    showSuccessToast,
    super8PlayersForCurrentChallenge,
  });

  const saveDrawPairsForChallenge = useCallback(
    async (
      pairsToSave = draftPairs,
      options: { silent?: boolean } = {},
    ) => {
      if (!token || !challengeDetail || savingDrawPairs) return false;
      const normalizedPairs = pairsToSave
        .map((pair) => {
          const cpf1 = normalizeCpf(pair.cpf1);
          const cpf2 = normalizeCpf(pair.cpf2);
          return {
            ...pair,
            cpf1,
            cpf2,
            id: pair.id || createPairId(cpf1, cpf2),
            label:
              pair.label ||
              `${nameByCpf[cpf1] || siteTexts.challengeEngine.labels.player} / ${
                nameByCpf[cpf2] || siteTexts.challengeEngine.labels.player
              }`,
          };
        })
        .filter((pair) => pair.cpf1 && pair.cpf2 && pair.cpf1 !== pair.cpf2);
      if (normalizedPairs.length === 0) {
        showErrorToast(siteTexts.challengeEngine.labels.nonePairDrawn);
        return false;
      }

      const selectedCpfs = normalizedPairs.flatMap((pair) => [
        pair.cpf1,
        pair.cpf2,
      ]);
      const uniqueCpfs = new Set(selectedCpfs);
      if (uniqueCpfs.size !== selectedCpfs.length) {
        showErrorToast("Um jogador não pode aparecer em mais de uma dupla.");
        return false;
      }

      setSavingDrawPairs(true);
      try {
        console.info("challenge_pairs.save_started", {
          challengeId: challengeDetail.id,
          pairsCount: normalizedPairs.length,
          playersCount: uniqueCpfs.size,
        });
        const savedPairs = [];
        for (const pair of normalizedPairs) {
          const name1 =
            nameByCpf[pair.cpf1] || siteTexts.challengeEngine.labels.player;
          const name2 =
            nameByCpf[pair.cpf2] || siteTexts.challengeEngine.labels.player;
          await Promise.all([
            upsertPlayerProfile(pair.cpf1, name1),
            upsertPlayerProfile(pair.cpf2, name2),
          ]);
          const savedPair = await createPairForChallenge(
            challengeDetail.challengeType,
            pair.cpf1,
            pair.cpf2,
          );
          savedPairs.push({
            ...pair,
            id: savedPair.id || pair.id,
            label: `${name1} / ${name2}`,
          });
        }

        const participantCpfs = Array.from(
          new Set(savedPairs.flatMap((pair) => [pair.cpf1, pair.cpf2])),
        );
        const pairIds = savedPairs.map((pair) => pair.id);
        const organizerUserId = Boolean(challengeDetail.isOfficial ?? true)
          ? null
          : (challengeDetail.organizerUserId ?? null);
        const updated = await updateChallengeById(token, challengeDetail.id, {
          ...buildChallengePersistencePayload({
            challengeDetail,
            challengeEventDraft,
            draftPairs: savedPairs,
            allowTnpEventIdentity: isAdmin,
            organizerUserId,
            participantCpfs,
          }),
          finishedAt: challengeDetail.finishedAt,
          pairIds,
          participantCpfs,
        });
        const normalized = normalizeChallengeDetail(updated);
        const savedPairIds = new Set(pairIds);
        const savedPairById = new Map(
          savedPairs.map((pair) => [pair.id, pair]),
        );
        const responsePairs = (normalized.pairs || [])
          .filter((pair) => savedPairIds.has(pair.id))
          .map((pair) => ({
            ...pair,
            label: pair.label || savedPairById.get(pair.id)?.label || "",
          }));
        const nextPairs =
          responsePairs.length === savedPairs.length ? responsePairs : savedPairs;

        setChallengeDetail({
          ...normalized,
          pairs: nextPairs,
          participantCpfs,
        });
        setDraftPairs(nextPairs);
        setParticipantsFromPairs(nextPairs);
        console.info("challenge_pairs.save_succeeded", {
          challengeId: challengeDetail.id,
          pairIds,
        });
        if (!options.silent) {
          showSuccessToast(siteTexts.challengeEngine.labels.drawPairsSaved);
        }
        return true;
      } catch (error) {
        console.error("challenge_pairs.save_failed", {
          challengeId: challengeDetail.id,
          error,
        });
        showErrorToast(
          error instanceof Error && error.message
            ? error.message
            : siteTexts.challengeEngine.labels.drawPairsSaveError,
        );
        return false;
      } finally {
        setSavingDrawPairs(false);
      }
    },
    [
      challengeDetail,
      challengeEventDraft,
      draftPairs,
      isAdmin,
      nameByCpf,
      savingDrawPairs,
      setChallengeDetail,
      setDraftPairs,
      setParticipantsFromPairs,
      showErrorToast,
      showSuccessToast,
      token,
    ],
  );

  const removePairFromChallenge = useCallback(
    (pairId: string) => {
      const next = draftPairs.filter((item) => item.id !== pairId);
      setDraftPairs(next);
      setParticipantsFromPairs(next);
    },
    [draftPairs, setDraftPairs, setParticipantsFromPairs],
  );

  const removePlayerFromChallenge = useCallback(
    (cpf: string) => {
      const next = draftPlayers.filter((item) => item.cpf !== cpf);
      setDraftPlayers(next);
      setParticipantsFromPlayers(next);
      setDraftPairs([]);
    },
    [draftPlayers, setDraftPairs, setDraftPlayers, setParticipantsFromPlayers],
  );

  const updatePlayerSideForChallenge = useCallback(
    async (cpf: string, sideChoice: "direita" | "esquerda" | "ambos") => {
      const normalizedCpf = normalizeCpf(cpf);
      const nextSide = sideChoiceToDraftSide(sideChoice);
      let nextPlayers: typeof draftPlayers = [];
      setDraftPlayers((prev) => {
        nextPlayers = prev.map((player) =>
          normalizeCpf(player.cpf) === normalizedCpf
            ? { ...player, side: nextSide }
            : player,
        );
        return nextPlayers;
      });
      if (nextPlayers.length > 0) {
        setParticipantsFromPlayers(nextPlayers);
        setDraftPairs([]);
      }

      const registration = challengeRegistrationByCpf[normalizedCpf];
      const registrationId = Number(registration?.id || 0);
      if (!token || !Number.isFinite(registrationId) || registrationId <= 0) {
        return;
      }
      try {
        await updateEventRegistrationPreferredSide(
          token,
          registrationId,
          sideChoice,
        );
        const eventId = String(
          manualRegistrationEventIdForChallenge || "",
        ).trim();
        if (eventId) {
          await loadChallengeEventRegistrations(eventId);
        }
        await loadEventRegistrations();
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Não foi possível atualizar a preferência de lado.";
        showErrorToast(message);
      }
    },
    [
      challengeRegistrationByCpf,
      loadChallengeEventRegistrations,
      loadEventRegistrations,
      manualRegistrationEventIdForChallenge,
      setDraftPairs,
      setDraftPlayers,
      setParticipantsFromPlayers,
      showErrorToast,
      token,
    ],
  );

  const removeSuper8PlayerFromChallenge = useCallback(
    (cpf: string) => {
      const normalizedCpf = normalizeCpf(cpf);
      const next = draftPlayers.filter(
        (item) => normalizeCpf(item.cpf) !== normalizedCpf,
      );
      setDraftPlayers(next);
      setParticipantsFromPlayers(next);
      setDraftPairs([]);
    },
    [draftPlayers, setDraftPairs, setDraftPlayers, setParticipantsFromPlayers],
  );

  useEffect(() => {
    if (!showChallengeModal) return;
    if (currentPairMode !== "draw" || isSuper8) return;
    if (
      String(challengeDetail?.challengeType || "").trim().toLowerCase() !==
      "challenge"
    ) {
      return;
    }
    if (draftPairs.length > 0) return;
    const restoredPairs = (challengeDetail?.pairs || [])
      .map((pair) => ({
        id: String(pair.id || "").trim(),
        cpf1: normalizeCpf(pair.cpf1),
        cpf2: normalizeCpf(pair.cpf2),
        label: String(pair.label || "").trim(),
      }))
      .filter((pair) => pair.id && pair.cpf1 && pair.cpf2);
    if (restoredPairs.length === 0) return;
    setDraftPairs(restoredPairs);
  }, [
    challengeDetail?.challengeType,
    challengeDetail?.pairs,
    currentPairMode,
    draftPairs.length,
    isSuper8,
    setDraftPairs,
    showChallengeModal,
  ]);

  const { onFinishChallenge, onSaveChallenge, onStartChallenge } =
    useAdminChallengePersistence({
      autoCreateMissingRegistrationsAndCharges,
      canStartChallenge,
      challengeDetail,
      challengeDurationDraft,
      challengeEventDraft,
      challengeIdFromRoute,
      challengeRoundsJson,
      challengeScheduledAtDraft,
      challengeScheduledEndAtDraft,
      challengeSelectedParticipants,
      challengeTournamentCategories,
      currentPairMode,
      draftPairs,
      goToAdminListPage,
      isAdmin,
      isPoco,
      loadHistory,
      minimumParticipants,
      resolveParticipantCpfsForPersist,
      savingChallenge,
      setChallengeDetail,
      setChallengeEventDraft,
      setChallengeModalMode,
      setChallengeRoundsJson,
      setContent,
      setEventChallengeLinkDrafts,
      setEventChallengeLinks,
      setSavingChallenge,
      setSelectedChallengeId,
      setShowChallengeModal,
      showErrorToast,
      showSuccessToast,
      token,
    });

  const generateFixedPairRoundsPersisted = useCallback(async () => {
    if (currentPairMode === "draw" && !isSuper8) {
      const saved = await saveDrawPairsForChallenge(draftPairs, {
        silent: true,
      });
      if (!saved) return false;
    }
    console.info("challenge_rounds.generate_started", {
      challengeId: challengeDetail?.id,
      pairsCount: draftPairs.length,
      pairMode: currentPairMode,
    });
    const generatedRounds = generateFixedPairRounds();
    if (!generatedRounds || generatedRounds.length === 0) return false;
    const saved = await onSaveChallenge({
      includeRounds: true,
      roundsOverride: generatedRounds,
      roundsOnly: true,
    });
    if (!saved) return false;
    console.info("challenge_rounds.generate_succeeded", {
      challengeId: challengeDetail?.id,
      pairsCount: draftPairs.length,
      pairMode: currentPairMode,
    });
    return true;
  }, [
    challengeDetail?.id,
    currentPairMode,
    draftPairs,
    generateFixedPairRounds,
    isSuper8,
    onSaveChallenge,
    saveDrawPairsForChallenge,
  ]);

  const generatePlayerBasedRoundsPersisted = useCallback(async () => {
    const generated = generatePlayerBasedRounds();
    if (!generated || generated.pairs.length === 0) return false;
    if (isSuper8) {
      if (!generated.rounds || generated.rounds.length === 0) return false;
      const pairsSaved = await saveDrawPairsForChallenge(generated.pairs, {
        silent: true,
      });
      if (!pairsSaved) return false;
      return onSaveChallenge({
        includeRounds: true,
        roundsOverride: generated.rounds,
        roundsOnly: true,
      });
    }
    setPairMode("draw");
    const saved = await saveDrawPairsForChallenge(generated.pairs);
    setPairMode("draw");
    return saved;
  }, [
    generatePlayerBasedRounds,
    isSuper8,
    onSaveChallenge,
    saveDrawPairsForChallenge,
    setPairMode,
  ]);

  const {
    onConfirmDeleteChallengeFromList,
    onDeleteChallenge,
    onRequestDeleteChallengeFromList,
  } = useAdminChallengeDeletion({
    challengeDetail,
    challengeIdFromRoute,
    confirmDeleteChallengeTarget,
    goToAdminListPage,
    loadHistory,
    selectedChallengeId,
    setChallengeDetail,
    setChallengeRoundsJson,
    setConfirmDeleteChallengeError,
    setConfirmDeleteChallengeLoading,
    setConfirmDeleteChallengeTarget,
    setSelectedChallengeId,
    setShowChallengeModal,
    showErrorToast,
    showSuccessToast,
    token,
  });

  if (loading) {
    return (
      <AppLayout title={siteTexts.header.nav.admin}>
        <Spinner label={siteTexts.common.loading} />
      </AppLayout>
    );
  }

  if (!isStaff) {
    return (
      <AppLayout title={siteTexts.header.nav.admin}>
        <EmptyState message={t.accessDenied} />
      </AppLayout>
    );
  }

  if (contentRouteMode && !isAdmin) {
    return (
      <AppLayout title={t.tabs.content}>
        <EmptyState message={t.accessDenied} />
      </AppLayout>
    );
  }

  const { pageSubtitle, pageTitle } = getAdminPageHeaderMeta({
    challengeIdFromRoute,
    challengeRouteParams,
    contentRouteMode,
    isAdmin,
  });
  const historyNavActive =
    !contentRouteMode && activeTab === "history" && !isTournamentsRouteView;
  const tournamentsNavActive =
    !contentRouteMode && activeTab === "history" && isTournamentsRouteView;
  const hideMainPanelHeader = true;
  const finishedChallengePanelProps = challengeDetail
    ? ({
        activeTournamentCategoryKey,
        categoryOptions: CHALLENGE_CATEGORIES,
        challengeDetail,
        categories: challengeTournamentCategories,
        genderOptions: CATEGORY_GENDERS,
        isAdmin,
        isPoco,
        isSuper8,
        isTournamentChallenge,
        onBackToResults: () => setChallengeModalMode("results"),
        onError: showErrorToast,
        onSaveChallenge,
        onSuccess: showSuccessToast,
        onUpdatePocoWinner: updatePocoWinner,
        onUpdateRoundScore: updateRoundScore,
        pairLabelById,
        resolveRoundPairLabelWithPoints,
        rounds: roundsParsed,
        savingChallenge,
        scoreMax,
        setChallengeDetail,
        setChallengeRoundsJson,
        token: token || "",
      } satisfies ComponentProps<typeof AdminFinishedChallengePanel>)
    : null;
  const challengeEditorPanelProps = challengeDetail
    ? ({
        activeTournamentCategory,
        activeTournamentCategoryKey,
        activeTournamentCategoryPairCapacity,
        adminPaymentMethod,
        availableUsersForChallenge,
        canAddRegistrationsForChallenge,
        canFinishChallenge,
        canGenerateFixedPairRounds,
        canGeneratePlayerBasedRounds,
        canStartChallenge,
        categoryGenderLabel,
        categoryLabel,
        categoryOptions: CHALLENGE_CATEGORIES,
        categorySaveRevision,
        challengeDetail,
        challengeDurationDraft,
        challengeEventDraft,
        challengeEventRegistrationsLoading,
        challengeRegistrationByCpf,
        challengeRegistrationByPairId,
        challengeScheduledAtDraft,
        challengeScheduledEndAtDraft,
        challengeSelectedParticipantsCount: challengeSelectedParticipants.length,
        challengeTournamentCategories,
        currentUserId: authUser?.id || null,
        currentPairMode,
        draftPairs,
        draftPlayers,
        drawCpf,
        genderOptions: CATEGORY_GENDERS,
        isAdmin,
        isFixedPairOnlyChallenge: isFixedPairOnlyChallengeType(
          challengeDetail.challengeType,
        ),
        isPoco,
        isSuper8,
        hasRegistrationCategories,
        isTournamentChallenge,
        manualRegistrationEventIdForChallenge,
        maxDrawPlayersForCurrentChallenge,
        maxFixedPairsForCurrentChallenge,
        nameByCpf,
        onAddDrawPlayer: addDrawPlayer,
        onAddFixedPair: addFixedPair,
        onAddTournamentCategory: addTournamentCategoryOnChallenge,
        onClearRoundWinner: (roundIndex, matchIndex) =>
          applyQuickWinnerForRoundMatch(roundIndex, matchIndex, ""),
        onCopyPaymentLink,
        onDeleteChallenge,
        onDeleteRegistration,
        onEditTournamentCategory: editTournamentCategoryOnChallenge,
        onEditTournamentPair: openTournamentPairModal,
        onError: showErrorToast,
        onExportShirtSizes: exportChallengeShirtSizes,
        onFinishChallenge,
        onGenerateFixedPairRounds: generateFixedPairRoundsPersisted,
        onGeneratePlayerBasedRounds: generatePlayerBasedRoundsPersisted,
        onStartChallenge,
        onToggleRegistrations: () =>
          onToggleChallengeRegistrationsFromList(challengeDetail),
        onLoadChallengeEventRegistrations: loadChallengeEventRegistrations,
        onMarkPaidRegistration,
        onRejectRegistration,
        onRemovePair: removePairFromChallenge,
        onRemovePlayer: removePlayerFromChallenge,
        onRemoveSuper8Player: removeSuper8PlayerFromChallenge,
        onRemoveTournamentCategory: removeTournamentCategoryOnChallenge,
        onCloseTournamentCategoryModal: () => setTournamentCategoryModal(null),
        onSaveChallenge,
        onSaveAvailabilityRestriction,
        onSaveTournamentCategory: saveTournamentCategoryModal,
        onSelectTournamentCategory: selectTournamentCategoryForRegistrations,
        onSuccess: showSuccessToast,
        onUpdatePlayerSide: updatePlayerSideForChallenge,
        onUpdatePocoWinner: updatePocoWinner,
        onUpdateRoundScore: updateRoundScore,
        organizerUsers,
        pairCpf1,
        pairCpf2,
        pairLabelById,
        pairMode,
        resolvePlayerCpfInput: resolveChallengePlayerCpfInput,
        resolveRoundPairLabelWithPoints,
        resolveSideLabel: formatSideLabel,
        rounds: roundsParsed,
        registrationsToggleLoading: Boolean(
          challengeRegistrationsToggleLoadingByEventId[
            resolveChallengeEventId(challengeDetail)
          ],
        ),
        savingChallenge,
        savingDrawPairs,
        scoreMax,
        setChallengeDetail,
        setChallengeDurationDraft,
        setChallengeEventDraft,
        setChallengeRoundsJson,
        setChallengeScheduledAtDraft,
        setChallengeScheduledEndAtDraft,
        setDrawCpf,
        setPairCpf1,
        setPairCpf2,
        setPairMode,
        showClearRoundWinnerAction: true,
        super8GroupAPlayers,
        super8WaitingPlayers,
        token: token || "",
        tournamentCategoryModal,
        tournamentParticipantsSummaryByCategory,
        updateTournamentCategoryModalDraft,
      } satisfies ComponentProps<typeof AdminChallengeEditorPanel>)
    : null;

  return (
    <AppLayout
      title={hideMainPanelHeader ? undefined : pageTitle}
      subtitle={hideMainPanelHeader ? undefined : pageSubtitle}
      headerRight={
        !hideMainPanelHeader && contentRouteMode ? (
          <Button type="button" variant="pill" href="#/admin">
            {t.sidebar.backToPanel}
          </Button>
        ) : undefined
      }
    >
      <div
        className={`admin-shell${adminNavOpen ? " nav-open" : ""}${
          adminNavCollapsed ? " nav-collapsed" : ""
        }`}
      >
        <AdminSidebar
          adminNavCollapsed={adminNavCollapsed}
          adminNavOpen={adminNavOpen}
          categoryRequestsNavActive={
            !contentRouteMode && activeTab === "categoryRequests"
          }
          couponsNavActive={!contentRouteMode && activeTab === "coupons"}
          clubsNavActive={!contentRouteMode && activeTab === "clubs"}
          historyNavActive={historyNavActive}
          isAdmin={isAdmin}
          leaguesNavActive={Boolean(leaguesRouteMode)}
          isOrganizer={isOrganizer}
          onClose={closeAdminNav}
          onNavigateCategoryRequests={() =>
            navigateAdminTab("categoryRequests")
          }
          onNavigateClubs={() => navigateAdminTab("clubs")}
          onNavigateCoupons={() => navigateAdminTab("coupons")}
          onNavigateHistory={() => navigateAdminTab("history")}
          onNavigatePayments={() => navigateAdminTab("payments")}
          onNavigateRegistrations={() => navigateAdminTab("registrations")}
          onNavigateTournaments={navigateAdminTournaments}
          onNavigateUsers={() => navigateAdminTab("users")}
          onToggleDesktop={toggleAdminDesktopNav}
          paymentsNavActive={!contentRouteMode && activeTab === "payments"}
          registrationsNavActive={
            !contentRouteMode && activeTab === "registrations"
          }
          tournamentsNavActive={tournamentsNavActive}
          usersNavActive={!contentRouteMode && activeTab === "users"}
        />

        <div className="admin-main">
          <div className="admin-mobile-nav-trigger">
            {!adminNavOpen && (
              <Button type="button" variant="pill" onClick={toggleAdminNav}>
                Menu do painel
              </Button>
            )}
          </div>

          {toast && (
            <Toast
              message={toast.message}
              variant={toast.variant}
              onClose={() => setToast(null)}
            />
          )}
          {tournamentCategoryModal &&
            String(challengeDetail?.challengeType || "")
              .trim()
              .toLowerCase() !== "tournament" && (
              <TournamentCategoryModal
                modal={tournamentCategoryModal}
                categoryOptions={CHALLENGE_CATEGORIES}
                genderOptions={CATEGORY_GENDERS}
                onChangeDraft={updateTournamentCategoryModalDraft}
                onClose={() => setTournamentCategoryModal(null)}
                onSave={saveTournamentCategoryModal}
                saving={savingChallenge}
                submitLabel={siteTexts.common.save}
                categoryLabel={categoryLabel}
                categoryGenderLabel={categoryGenderLabel}
                token={token}
                showCategoryTime={
                  ["challenge", "poco"].includes(
                    String(challengeDetail?.challengeType || "")
                      .trim()
                      .toLowerCase(),
                  )
                }
                categoryDate={challengeScheduledAtDraft}
                acceptLegacyPocoTime={
                  String(challengeDetail?.challengeType || "")
                    .trim()
                    .toLowerCase() === "poco"
                }
                categoryTimeMode={
                  ["challenge", "poco"].includes(
                    String(challengeDetail?.challengeType || "")
                      .trim()
                      .toLowerCase(),
                  )
                    ? "timeOnly"
                    : "dateTime"
                }
                lockRegistrationType={
                  String(challengeDetail?.challengeType || "")
                    .trim()
                    .toLowerCase() === "poco"
                }
                lockSlotLimit={
                  ["challenge", "poco"].includes(
                    String(challengeDetail?.challengeType || "")
                      .trim()
                      .toLowerCase(),
                  )
                }
              />
            )}
          {tournamentPairModal && (
            <TournamentPairModal
              modal={tournamentPairModal}
              saving={tournamentPairModalSaving}
              categories={challengeTournamentCategories}
              availableUsers={availableUsersForTournamentPairModal}
              eventShirtSizesEnabled={Boolean(
                challengeEventDraft.shirtSizesEnabled,
              )}
              eventShirtSizes={challengeEventDraft.shirtSizes || []}
              availabilityRestrictionsEnabled={Boolean(
                challengeEventDraft.availabilityRestrictionsEnabled,
              )}
              availabilityRestrictionPeriods={
                challengeAvailabilityRestrictionPeriods
              }
              availabilityRestrictionAmountCents={
                challengeAvailabilityRestrictionAmountCents
              }
              nameByCpf={nameByCpf}
              paymentMethod={adminPaymentMethod}
              onPaymentMethodChange={setAdminPaymentMethod}
              onClose={closeTournamentPairModal}
              onSubmit={submitTournamentPairModal}
              onChange={setTournamentPairModal}
              normalizeCpf={normalizeCpf}
              resolvePlayerCpfInput={resolveChallengePlayerCpfInput}
            />
          )}

          {isAdmin && contentRouteMode && activeTab === "content" && (
            <AdminContentPanel
              addListItem={addListItem}
              contentDraft={contentDraft}
              contentTab={contentTab}
              eventChallengeLinkDrafts={eventChallengeLinkDrafts}
              eventChallengeLinks={eventChallengeLinks}
              eventChallengeLinksLoading={eventChallengeLinksLoading}
              linkableEventChallengesSorted={linkableEventChallengesSorted}
              onDeleteEventLink={onDeleteEventLink}
              onUpsertEventLink={onUpsertEventLink}
              removeHeroImage={removeHeroImage}
              removeListImage={removeListImage}
              removeListItem={removeListItem}
              saveContent={saveContent}
              savingContent={savingContent}
              setContentTab={setContentTab}
              setEventChallengeLinkDrafts={setEventChallengeLinkDrafts}
              updateAbout={updateAbout}
              updateHero={updateHero}
              updateListItem={updateListItem}
            />
          )}

          {activeTab === "leagues" && <LeagueManagementPanel />}

          {activeTab === "users" && (
            <AdminUsersTab
              isAdmin={isAdmin}
              isOrganizer={isOrganizer}
              users={users}
              filteredUsers={filteredUsers}
              usersLoading={usersLoading}
              userSearch={userSearch}
              setUserSearch={setUserSearch}
              userDrafts={userDrafts}
              setUserDrafts={setUserDrafts}
              buildUserDraft={buildUserDraft}
              authUserId={authUser?.id}
              onSaveUser={onSaveUser}
              onDeleteUser={onDeleteUser}
              onOpenCreateUserModal={() => setShowCreateUserModal(true)}
              showCreateUserModal={showCreateUserModal}
              onDismissCreateUserModal={onDismissCreateUserModal}
              newUser={newUser}
              setNewUser={setNewUser}
              onCreateUser={onCreateUser}
              onCancelCreateUser={onCancelCreateUser}
              asaasOrganizers={asaasOrganizers}
              onSaveOrganizerWallet={onSaveOrganizerWallet}
              paymentsLoading={paymentsLoading}
              setAsaasOrganizers={setAsaasOrganizers}
            />
          )}

          {activeTab === "registrations" && (
            <AdminRegistrationsPanel
              eventRegistrations={eventRegistrations}
              eventRegistrationsLoading={eventRegistrationsLoading}
              eventRegistrationsStatus={eventRegistrationsStatus}
              isAdmin={isAdmin}
              onCopyPaymentLink={onCopyPaymentLink}
              onDeleteRegistration={onDeleteRegistration}
              onLoadEventRegistrations={loadEventRegistrations}
              onMarkPaidRegistration={onMarkPaidRegistration}
              onSaveAvailabilityRestriction={onSaveAvailabilityRestriction}
              onRejectRegistration={onRejectRegistration}
              setEventRegistrationsStatus={setEventRegistrationsStatus}
            />
          )}

          {activeTab === "categoryRequests" && isAdmin && (
            <AdminCategoryRequestsPanel
              adminNotes={categoryChangeAdminNotes}
              loading={categoryChangeRequestsLoading}
              onLoad={loadCategoryChangeRequests}
              onResolve={onResolveCategoryChangeRequest}
              requests={categoryChangeRequests}
              resolveLoadingId={categoryChangeResolveLoadingId}
              setAdminNotes={setCategoryChangeAdminNotes}
              setStatus={setCategoryChangeRequestsStatus}
              status={categoryChangeRequestsStatus}
            />
          )}

          {activeTab === "payments" && (isAdmin || isOrganizer) && (
            <AdminPaymentsPanel
              asaasSettingsDraft={asaasSettingsDraft}
              isAdmin={isAdmin}
              isOrganizer={isOrganizer}
              myAsaasWalletDraft={myAsaasWalletDraft}
              onSaveAsaasSettings={onSaveAsaasSettings}
              onSaveMyWallet={onSaveMyWallet}
              paymentsLoading={paymentsLoading}
              setAsaasSettingsDraft={setAsaasSettingsDraft}
              setMyAsaasWalletDraft={setMyAsaasWalletDraft}
            />
          )}

          {activeTab === "clubs" && isAdmin && token && (
            <AdminClubsPanel
              token={token}
              onError={showErrorToast}
              onSuccess={showSuccessToast}
            />
          )}

          {activeTab === "coupons" && isAdmin && token && (
            <AdminDiscountCoupons
              token={token}
              onError={showErrorToast}
              onSuccess={showSuccessToast}
            />
          )}

          {activeTab === "history" && (
            <div className="admin-content">
              {!challengeIdFromRoute && (
                <AdminHistoryPanel
                  availableTypes={CHALLENGE_TYPES_ONLY}
                  challengeRegistrationsOpenByEventId={challengeRegistrationsOpenByEventId}
                  challengeRegistrationsToggleLoadingByEventId={challengeRegistrationsToggleLoadingByEventId}
                  confirmDeleteChallengeError={confirmDeleteChallengeError}
                  confirmDeleteChallengeLoading={confirmDeleteChallengeLoading}
                  confirmDeleteChallengeTarget={confirmDeleteChallengeTarget}
                  dateFrom={historyDateFrom}
                  dateTo={historyDateTo}
                  effectiveTypeFilters={effectiveHistoryTypeFilters}
                  filteredHistory={filteredHistory}
                  historyLoading={historyLoading}
                  historyOfficialFilter={historyOfficialFilter}
                  historyOrganizerUserIdFilter={historyOrganizerUserIdFilter}
                  historyTotal={historyTotal}
                  isAdmin={isAdmin}
                  isTournamentsRouteView={isTournamentsRouteView}
                  nameFilter={historyNameFilter}
                  onConfirmDeleteChallenge={onConfirmDeleteChallengeFromList}
                  onFinishChallenge={onFinishChallengeFromList}
                  onOpenChallenge={openChallengeRoute}
                  onRequestDeleteChallenge={onRequestDeleteChallengeFromList}
                  onStartChallenge={onStartChallengeFromList}
                  onToggleChallengeRegistrations={onToggleChallengeRegistrationsFromList}
                  organizerUsers={organizerUsers}
                  resolveChallengeEventId={resolveChallengeEventId}
                  selectedChallengeId={selectedChallengeId}
                  setConfirmDeleteChallengeTarget={setConfirmDeleteChallengeTarget}
                  finishingChallengeIds={finishingChallengeIdsFromList}
                  startingChallengeIds={startingChallengeIdsFromList}
                  setDateFrom={setHistoryDateFrom}
                  setDateTo={setHistoryDateTo}
                  setHistoryOfficialFilter={setHistoryOfficialFilter}
                  setHistoryOrganizerUserIdFilter={setHistoryOrganizerUserIdFilter}
                  setNameFilter={setHistoryNameFilter}
                  statusFilters={historyStatusFilters}
                  toggleStatus={toggleHistoryStatus}
                  toggleType={toggleHistoryType}
                />
              )}

              {challengeIdFromRoute &&
                (!showChallengeModal || !challengeDetail) && (
                  <AdminSection>
                    <Spinner
                      label={
                        isTournamentsRouteView
                          ? t.historyList.loadingTournament
                          : t.historyList.loadingChallenge
                      }
                    />
                  </AdminSection>
                )}

              {showChallengeModal &&
                challengeDetail &&
                finishedChallengePanelProps &&
                challengeEditorPanelProps && (
                  <AdminChallengeModalPanel
                    challengeDetail={challengeDetail}
                    challengeIdFromRoute={challengeIdFromRoute}
                    challengeModalMode={challengeModalMode}
                    editorPanelProps={challengeEditorPanelProps}
                    finishedPanelProps={finishedChallengePanelProps}
                    onBackToEdit={() => setChallengeModalMode("edit")}
                    onClose={onCloseChallengeModal}
                  />
                )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
